import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getApiV1WeddingsByWeddingIdDisplayActivitiesWall,
  getApiV1WeddingsByWeddingIdDisplayAttendees,
} from '@wedding-tech/api-client'
import {
  getApiV1WeddingsByWeddingIdDisplayActivitiesWallQueryKey,
  getApiV1WeddingsByWeddingIdDisplayAttendeesQueryKey,
} from '@wedding-tech/api-client/tanstack'
import { useEffect, useMemo, useRef, useState } from 'react'

import { normalizeWallState, type WallState } from '@/lib/wall-state'

/**
 * How often the wall re-reads each resource. There is no WebSocket transport
 * yet (internal/adapters/realtime is a skeleton with no route), so the
 * projector polls — see docs/migration-plan.md.
 *
 * The two cadences differ because the data does: spin/winner state changes the
 * instant an operator presses a button and must look immediate, while the
 * attendee roster only grows as people physically arrive.
 */
const WALL_POLL_MS = 2000
const ATTENDEES_POLL_MS = 20000

export type BubbleState = {
  /** LINE user id — the per-person render and physics key. */
  id: string
  /**
   * The party this person belongs to. The backoffice picks a winner by party
   * id, so this — not `id` — is what a winner matches against.
   */
  guestId: string
  name: string
  pictureUrl?: string
  maritalStatus?: string
  initialX: number
  initialY: number
  vx: number
  vy: number
  initialScale: number
  scaleDuration: number
}

/**
 * Carries the HTTP status a display request failed with.
 *
 * The generated client is configured with `throwOnError: true`, which throws
 * the *parsed error body* — for this API a bare string like "not found" — with
 * no status attached. That is not enough to tell "this screen is not
 * authorized" from "the wifi blipped", so these queries opt out with
 * `throwOnError: false` and re-throw the status themselves.
 */
class DisplayRequestError extends Error {
  status: number

  constructor(status: number) {
    super(`display request failed with status ${status}`)
    this.name = 'DisplayRequestError'
    this.status = status
  }
}

/**
 * True only for a 404 — the one error that means the screen is not authorized
 * (token absent, expired, revoked, or minted for another wedding; the API
 * deliberately refuses to say which).
 *
 * Every other failure is transient and must NOT blank the wall: the shared
 * client sets `retry: false` and this polls every couple of seconds, so one
 * hiccup would otherwise replace a running projector with an error card.
 */
function isUnauthorizedError(error: unknown): boolean {
  return error instanceof DisplayRequestError && error.status === 404
}

/** Physics seeds for a newly-arrived bubble. */
function seedBubble(
  id: string,
  guestId: string,
  name: string,
  pictureUrl?: string,
  maritalStatus?: string,
): BubbleState {
  return {
    id,
    guestId,
    name,
    pictureUrl,
    maritalStatus,
    initialX: Math.random() * 80 + 10,
    initialY: Math.random() * 80 + 10,
    vx: (Math.random() - 0.5) * 0.004,
    vy: (Math.random() - 0.5) * 0.004,
    initialScale: 0.8 + Math.random() * 0.4,
    scaleDuration: 5 + Math.random() * 5,
  }
}

export function useWallDisplayData(weddingId: string, token: string | null) {
  const queryClient = useQueryClient()

  const request = { path: { weddingId }, query: { dt: token ?? undefined } }

  const wallQuery = useQuery({
    queryKey: getApiV1WeddingsByWeddingIdDisplayActivitiesWallQueryKey(request),
    queryFn: async () => {
      const { data, response } =
        await getApiV1WeddingsByWeddingIdDisplayActivitiesWall({
          ...request,
          throwOnError: false,
        })
      if (!response.ok) throw new DisplayRequestError(response.status)
      return data
    },
    enabled: !!token,
    refetchInterval: WALL_POLL_MS,
    // A projector is usually a second screen on a laptop the operator is also
    // driving the backoffice from. The moment they click into the admin tab
    // this window is "background", and without this the wall would stop
    // polling exactly when they press Spin. The old RTDB listener was
    // push-based and had no such failure mode.
    refetchIntervalInBackground: true,
    // The shared client defaults to staleTime 30s (lib/query.tsx); on a
    // projector every poll must actually hit the network.
    staleTime: 0,
  })

  const attendeesQuery = useQuery({
    queryKey: getApiV1WeddingsByWeddingIdDisplayAttendeesQueryKey(request),
    queryFn: async () => {
      const { data, response } =
        await getApiV1WeddingsByWeddingIdDisplayAttendees({
          ...request,
          throwOnError: false,
        })
      if (!response.ok) throw new DisplayRequestError(response.status)
      return data
    },
    enabled: !!token,
    refetchInterval: ATTENDEES_POLL_MS,
    refetchIntervalInBackground: true,
    staleTime: 0,
  })

  const wallState: WallState = useMemo(
    () => normalizeWallState(wallQuery.data?.data),
    [wallQuery.data],
  )

  // Physics seeds are randomized once per attendee and then reused. Generating
  // them inside the map instead would re-roll every bubble's position on every
  // poll, so the whole field would teleport on a fixed interval.
  const bubbleCacheRef = useRef<Map<string, BubbleState>>(new Map())
  const [allCheckins, setAllCheckins] = useState<BubbleState[]>([])

  const attendees = attendeesQuery.data?.data
  useEffect(() => {
    if (!attendees) {
      setAllCheckins([])
      return
    }

    const cache = bubbleCacheRef.current
    const seen = new Set<string>()

    const bubbles = [...attendees]
      // The endpoint documents no ordering; this preserves what the old
      // Firestore query's orderBy('checkedInAt', 'desc') gave us.
      .sort((a, b) => (b.checkedInAt ?? '').localeCompare(a.checkedInAt ?? ''))
      .map((a) => {
        const id = a.lineUserId ?? ''
        seen.add(id)
        const guestId = a.guestId ?? ''

        const existing = cache.get(id)
        // Keep the seeds, refresh the metadata — a display name or picture can
        // change under us without the bubble having to jump.
        const bubble: BubbleState = existing
          ? {
              ...existing,
              guestId,
              name: a.displayName ?? '',
              pictureUrl: a.pictureUrl,
              maritalStatus: a.maritalStatus,
            }
          : seedBubble(
              id,
              guestId,
              a.displayName ?? '',
              a.pictureUrl,
              a.maritalStatus,
            )

        cache.set(id, bubble)
        return bubble
      })

    for (const id of cache.keys()) {
      if (!seen.has(id)) cache.delete(id)
    }

    setAllCheckins(bubbles)
  }, [attendees])

  // Auto-close is decided server-side on read (docs/adr/0003), so the display
  // never writes. It only needs to re-read at the deadline: polling would get
  // there within WALL_POLL_MS anyway, but landing on the exact moment keeps
  // the overlay from lingering a beat past its countdown.
  const { winner, winnerShownAt, settings } = wallState
  useEffect(() => {
    if (!winner || !winnerShownAt || !settings.autoClose) return

    const deadline = winnerShownAt + settings.autoCloseDelay * 1000
    const remaining = deadline - Date.now()
    if (remaining <= 0) return

    const timer = setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: getApiV1WeddingsByWeddingIdDisplayActivitiesWallQueryKey({
          path: { weddingId },
          query: { dt: token ?? undefined },
        }),
      })
    }, remaining)
    return () => clearTimeout(timer)
  }, [
    winner,
    winnerShownAt,
    settings.autoClose,
    settings.autoCloseDelay,
    queryClient,
    weddingId,
    token,
  ])

  return {
    wallState,
    allCheckins,
    // Only a missing token or a 404 takes the wall down. A transient failure
    // keeps the last good render: TanStack retains `data` across a failed
    // refetch, which is what lets the projector ride out flaky venue wifi the
    // way the old Firebase subscription did.
    isUnauthorized:
      !token ||
      isUnauthorizedError(wallQuery.error) ||
      isUnauthorizedError(attendeesQuery.error),
  }
}
